import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import Dexie, { Table } from 'dexie';
import {
  AuditDeliveryWarning,
  INVOICE_LOG_ACTION_TYPES,
  PostInvoiceAuditEvent,
  PostInvoiceBillingContext,
  PostInvoiceOutcome,
  PostInvoiceSource,
  PostInvoiceStage,
} from '@app/interface/log-invoices-interfaces';
import { enforceAuditEventBound, normalizeAuditError, sanitizeAuditContext } from '@app/shared/util/audit-sanitizer.util';
import { DataService } from './data.service';
import { LogInvoicesService } from './log-invoices.service';

export type OutboxStatus = 'pending' | 'permanent_error';

export interface OutboxRecord {
  eventId: string;
  /** Scope of the session that produced the event; replay only happens under the same scope. */
  companyCnpj: string;
  userId: string;
  event: PostInvoiceAuditEvent;
  status: OutboxStatus;
  attempts: number;
  lastError?: string;
  createdAt: number;
}

class PostInvoiceOutboxDb extends Dexie {
  events!: Table<OutboxRecord, string>;

  constructor() {
    super('post-invoice-audit-outbox');
    this.version(1).stores({ events: 'eventId, companyCnpj, userId, status, createdAt' });
  }
}

export interface RecordAuditParams {
  osId: string;
  attemptId: string;
  previousAttemptId?: string;
  source: PostInvoiceSource;
  stage: PostInvoiceStage;
  outcome: PostInvoiceOutcome;
  reason: string;
  invoicesRelated?: PostInvoiceBillingContext['invoicesRelated'];
  resource?: PostInvoiceBillingContext['resource'];
  results?: PostInvoiceBillingContext['results'];
  recoveryReason?: string;
  error?: unknown;
  errorContext?: Record<string, unknown>;
}

export interface RecordAuditResult {
  eventId: string;
  /** True only after the server acknowledged the event. */
  delivered: boolean;
  /** True when the event rests in the durable outbox (or, as a fallback, in memory). */
  queued: boolean;
  /** True when IndexedDB/quota could not hold the event: only memory + manual export remain. */
  storageUnavailable: boolean;
}

const MAX_IMMEDIATE_ATTEMPTS = 3;
const BACKOFF_MS = [300, 1000];
/** Statuses that will not heal by retrying; kept as explicit pendencies instead of looping. */
const PERMANENT_STATUSES = new Set([400, 401, 403, 404, 409, 413, 422]);

/**
 * Durable, idempotent delivery of post-invoice audit events (task 73015, design D3).
 *
 * - Every event is sanitized, stored in an IndexedDB outbox scoped by company/user, then sent with
 *   a stable eventId; it is removed ONLY after the server acknowledges it.
 * - Replays resend audit events only. Business effects (invoices, stock, titles) are never retried.
 * - Events from company A are never sent under company B's session.
 */
@Injectable({ providedIn: 'root' })
export class PostInvoiceAuditService {
  private _dataService = inject(DataService);
  private _logInvoices = inject(LogInvoicesService);
  private _db: PostInvoiceOutboxDb | null = null;
  /** Fallback when IndexedDB is unavailable; lives only as long as the page. */
  private _memory = new Map<string, OutboxRecord>();
  private _flushing: Promise<void> | null = null;

  /** Surfaced to the UI: events waiting for delivery under the current session scope. */
  readonly pendingCount = signal(0);
  /** True when the last write could not reach durable storage. */
  readonly storageUnavailable = signal(false);

  constructor() {
    try {
      this._db = new PostInvoiceOutboxDb();
    } catch (error) {
      console.error('[PostInvoiceAuditService] IndexedDB unavailable', error);
      this.storageUnavailable.set(true);
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => void this.flushPending());
    }
    this._dataService.ready$.subscribe(([user, company]) => {
      if (user && company) {
        void this.flushPending();
      }
    });
  }

  newAttemptId(): string {
    const uuid = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    return `att-${uuid}`;
  }

  /**
   * Builds, queues and tries to deliver one event. Never throws: callers decide what to do with
   * `delivered` (checkpoints require it before the next business effect).
   */
  async record(params: RecordAuditParams): Promise<RecordAuditResult> {
    const eventId = `evt-${this.newAttemptId().slice(4)}`;
    const user = this._dataService.user;
    const company = this._dataService.company;

    const event: PostInvoiceAuditEvent = {
      eventId,
      osId: params.osId,
      userId: user?.id ?? user?._id,
      reason: params.reason,
      actionType: INVOICE_LOG_ACTION_TYPES.POST_OS_BILLING,
      billingContext: sanitizeAuditContext({
        attemptId: params.attemptId,
        previousAttemptId: params.previousAttemptId,
        source: params.source,
        stage: params.stage,
        outcome: params.outcome,
        occurredAt: new Date().toISOString(),
        invoicesRelated: params.invoicesRelated,
        resource: params.resource,
        results: params.results,
        recoveryReason: params.recoveryReason,
      }),
      error: params.error === undefined ? undefined : normalizeAuditError(params.error, params.errorContext),
    };
    enforceAuditEventBound(event);

    const record: OutboxRecord = {
      eventId,
      companyCnpj: company?.cnpj,
      userId: event.userId,
      event,
      status: 'pending',
      attempts: 0,
      createdAt: Date.now(),
    };

    const stored = await this._store(record);
    const delivered = await this._deliverWithRetry(record);
    return { eventId, delivered, queued: stored.queued && !delivered, storageUnavailable: stored.storageUnavailable };
  }

  /**
   * Keeps events the backend could not persist itself (finalization audit warning) so they are
   * replayed from here. Audit only — the business outcome they describe already happened.
   */
  async enqueueBackendWarning(warning: AuditDeliveryWarning | undefined): Promise<void> {
    if (!warning?.pendingEvents?.length) {
      return;
    }
    const company = this._dataService.company;
    const user = this._dataService.user;
    for (const pending of warning.pendingEvents) {
      const event = enforceAuditEventBound({
        ...pending,
        actionType: INVOICE_LOG_ACTION_TYPES.POST_OS_BILLING as const,
        error: pending.error ? normalizeAuditError(pending.error) : undefined,
      } as PostInvoiceAuditEvent);
      const record: OutboxRecord = {
        eventId: event.eventId,
        companyCnpj: company?.cnpj,
        userId: user?.id ?? user?._id,
        event,
        status: 'pending',
        attempts: 0,
        createdAt: Date.now(),
      };
      await this._store(record);
    }
    void this.flushPending();
  }

  /** Replays pending events of the CURRENT company/user only. Safe to call repeatedly. */
  flushPending(): Promise<void> {
    if (this._flushing) {
      return this._flushing;
    }
    this._flushing = (async () => {
      try {
        const records = await this._pendingForSession();
        for (const record of records.filter(r => r.status === 'pending')) {
          await this._deliverWithRetry(record);
        }
      } catch (error) {
        console.error('[PostInvoiceAuditService] flush failed', error);
      } finally {
        await this._refreshCount();
        this._flushing = null;
      }
    })();
    return this._flushing;
  }

  /** Sanitized copy of everything still pending for the current session, for manual hand-off to support. */
  async exportPending(): Promise<string> {
    const records = await this._pendingForSession();
    return JSON.stringify(records.map(r => ({
      eventId: r.eventId,
      status: r.status,
      lastError: r.lastError,
      event: r.event,
    })), null, 2);
  }

  private _sameScope(record: OutboxRecord): boolean {
    const company = this._dataService.company;
    const user = this._dataService.user;
    return !!company && !!user && record.companyCnpj === company.cnpj && record.userId === (user.id ?? user._id);
  }

  private async _pendingForSession(): Promise<OutboxRecord[]> {
    const all = new Map<string, OutboxRecord>(this._memory);
    if (this._db) {
      try {
        for (const record of await this._db.events.toArray()) {
          all.set(record.eventId, record);
        }
      } catch (error) {
        console.error('[PostInvoiceAuditService] cannot read outbox', error);
      }
    }
    return Array.from(all.values()).filter(r => this._sameScope(r)).sort((a, b) => a.createdAt - b.createdAt);
  }

  private async _store(record: OutboxRecord): Promise<{ queued: boolean; storageUnavailable: boolean }> {
    this._memory.set(record.eventId, record);
    if (!this._db) {
      this.storageUnavailable.set(true);
      await this._refreshCount();
      return { queued: true, storageUnavailable: true };
    }
    try {
      await this._db.events.put(record);
      this._memory.delete(record.eventId);
      await this._refreshCount();
      return { queued: true, storageUnavailable: false };
    } catch (error) {
      // Quota/private-mode failure is NOT persistence: memory only survives this page.
      console.error('[PostInvoiceAuditService] outbox write failed', error);
      this.storageUnavailable.set(true);
      await this._refreshCount();
      return { queued: true, storageUnavailable: true };
    }
  }

  private async _remove(eventId: string): Promise<void> {
    this._memory.delete(eventId);
    try {
      await this._db?.events.delete(eventId);
    } catch (error) {
      console.error('[PostInvoiceAuditService] cannot remove acknowledged event', error);
    }
  }

  private async _update(record: OutboxRecord): Promise<void> {
    if (this._memory.has(record.eventId)) {
      this._memory.set(record.eventId, record);
    }
    try {
      await this._db?.events.put(record);
    } catch (error) {
      console.error('[PostInvoiceAuditService] cannot update outbox record', error);
    }
  }

  private async _refreshCount(): Promise<void> {
    const records = await this._pendingForSession();
    this.pendingCount.set(records.length);
  }

  private async _deliverWithRetry(record: OutboxRecord): Promise<boolean> {
    if (!this._sameScope(record)) {
      return false;
    }

    for (let attempt = 0; attempt < MAX_IMMEDIATE_ATTEMPTS; attempt++) {
      try {
        record.attempts++;
        await this._logInvoices.create(record.event);
        await this._remove(record.eventId);
        await this._refreshCount();
        return true;
      } catch (error) {
        const status = error instanceof HttpErrorResponse ? error.status : 0;
        record.lastError = error instanceof Error ? error.message : String(error);

        if (PERMANENT_STATUSES.has(status)) {
          // Validation/authorization/conflict will not heal by retrying: keep it visible instead.
          record.status = 'permanent_error';
          await this._update(record);
          await this._refreshCount();
          return false;
        }

        await this._update(record);
        if (attempt < MAX_IMMEDIATE_ATTEMPTS - 1) {
          await new Promise(resolve => setTimeout(resolve, BACKOFF_MS[attempt] ?? 1000));
        }
      }
    }

    await this._refreshCount();
    return false;
  }
}
